import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { BotaoExtrairXlsx } from '../../componentes/BotaoExtrairXlsx';
import { Botao, EstadoCarregando, EstadoErro, EstadoVazio } from '../../componentes/ui';
import {
  hojeIso,
  listarAtividades,
  listarAtividadesDoPeriodo,
  rotuloDoTipo,
  situacaoDoPrazo,
  type AtividadeDoCrm,
  type VisaoDeAtividades,
} from '../../servicos/crm';
import { diaLocal, type ColunaDaPlanilha } from '../../servicos/planilha';

/** As atividades da aba, com a situação por extenso — a cor da tela não vai para a planilha. */
const COLUNAS_DA_PLANILHA: readonly ColunaDaPlanilha<AtividadeDoCrm>[] = [
  { titulo: 'Prazo', formato: 'data', valor: (a) => a.prazo },
  { titulo: 'Hora', largura: 8, valor: (a) => a.hora },
  { titulo: 'Atividade', largura: 40, valor: (a) => a.titulo },
  { titulo: 'Tipo', largura: 14, valor: (a) => rotuloDoTipo(a.tipo) },
  { titulo: 'Empresa', largura: 40, valor: (a) => a.card.razaoSocial },
  { titulo: 'Responsável', largura: 26, valor: (a) => a.responsavel.nome },
  {
    titulo: 'Situação',
    largura: 12,
    valor: (a) => {
      if (a.concluidaEm !== null) return 'Concluída';
      const s = situacaoDoPrazo(a.prazo);
      return s === 'vencida' ? 'Atrasada' : s === 'hoje' ? 'Hoje' : 'Em aberto';
    },
  },
  { titulo: 'Concluída em', formato: 'data', largura: 14, valor: (a) => diaLocal(a.concluidaEm) },
  { titulo: 'Descrição', largura: 50, valor: (a) => a.descricao },
];

const ROTULO_DA_VISAO: Readonly<Record<VisaoDeAtividades, string>> = {
  hoje: 'Hoje e atrasadas',
  proximas: 'Próximas',
  concluidas: 'Concluídas',
};
import { Pagina } from '../Carteira';
import { CalendarioDeAtividades } from './CalendarioDeAtividades';
import { diasDaGrade, tituloDoMes, useMesDoCalendario } from './calendario';
import { FormularioDeAtividade, LinhaDeAtividade } from './ComponentesDeAtividade';

/**
 * CRM → Atividades: o que fazer hoje, o que já passou do prazo e o que vem.
 *
 * Abre em "Hoje e atrasadas", minhas — a pergunta de quem chega de manhã é
 * "o que eu tenho para hoje?". As atrasadas vêm primeiro: são as que já
 * custaram alguma coisa. A visão e o filtro ficam na URL, para o link servir.
 *
 * Em **Calendário** (`?modo=calendario`), as atividades do mês pelo prazo,
 * abertas e concluídas, com a cor da situação — a mesma grade do Pipeline.
 */
export function PaginaDeAtividades(): JSX.Element {
  const [parametros, setParametros] = useSearchParams();
  const visao: VisaoDeAtividades =
    parametros.get('visao') === 'proximas'
      ? 'proximas'
      : parametros.get('visao') === 'concluidas'
        ? 'concluidas'
        : 'hoje';
  const responsavel = parametros.get('de') === 'todos' ? 'todos' : 'eu';
  const calendario = parametros.get('modo') === 'calendario';
  const [editando, setEditando] = useState<AtividadeDoCrm | null>(null);

  const consulta = useQuery({
    queryKey: ['crm', 'atividades', visao, responsavel, hojeIso()],
    queryFn: () => listarAtividades({ visao, responsavel }),
    placeholderData: (anterior) => anterior,
    enabled: !calendario,
  });

  // O calendário pede as seis semanas da grade de uma vez.
  const { mes, mudar, irParaHoje } = useMesDoCalendario();
  const dias = diasDaGrade(mes);
  const de = dias[0]?.iso ?? hojeIso();
  const ate = dias[dias.length - 1]?.iso ?? hojeIso();
  const doPeriodo = useQuery({
    queryKey: ['crm', 'atividades', 'calendario', de, ate, responsavel],
    queryFn: () => listarAtividadesDoPeriodo({ de, ate, responsavel }),
    placeholderData: (anterior) => anterior,
    enabled: calendario,
  });
  const doMes = (doPeriodo.data?.itens ?? []).filter((a) =>
    dias.some((d) => d.doMes && d.iso === a.prazo),
  );

  const trocar = (chave: string, valor: string | null): void => {
    setParametros(
      (atual) => {
        const proximos = new URLSearchParams(atual);
        if (valor === null) proximos.delete(chave);
        else proximos.set(chave, valor);
        return proximos;
      },
      { replace: true },
    );
  };

  const dados = consulta.data;
  const c = dados?.contagens;
  const hoje = hojeIso();
  const atrasadas = (dados?.itens ?? []).filter((a) => a.prazo < hoje);
  const deHoje = (dados?.itens ?? []).filter((a) => a.prazo === hoje);

  const abas: readonly (readonly [VisaoDeAtividades, string])[] = [
    ['hoje', `Hoje e atrasadas${c === undefined ? '' : ` (${c.hoje + c.atrasadas})`}`],
    ['proximas', `Próximas${c === undefined ? '' : ` (${c.proximas})`}`],
    ['concluidas', 'Concluídas'],
  ];

  const lista = (itens: readonly AtividadeDoCrm[]): JSX.Element => (
    <ul className="flex flex-col gap-2">
      {itens.map((a) => (
        <LinhaDeAtividade key={a.id} atividade={a} mostrarEmpresa aoEditar={() => setEditando(a)} />
      ))}
    </ul>
  );

  return (
    <Pagina
      titulo="Atividades"
      descricao="As tarefas dos cards do pipeline: o que fazer, até quando e por quem. Crie atividades dentro de cada card."
      acao={
        <div className="flex flex-wrap items-center gap-3">
          <BotaoExtrairXlsx
            titulo={`Atividades - ${calendario ? tituloDoMes(mes) : ROTULO_DA_VISAO[visao]}${responsavel === 'eu' ? ' (minhas)' : ''}`}
            tamanho="pequeno"
            disabled={(calendario ? doPeriodo.data : consulta.data) === undefined}
            colunas={COLUNAS_DA_PLANILHA}
            linhas={calendario ? doMes : (consulta.data?.itens ?? [])}
          />
          <div role="group" aria-label="Como ver" className="flex gap-1">
            {(
              [
                ['lista', 'Lista'],
                ['calendario', 'Calendário'],
              ] as const
            ).map(([id, rotulo]) => (
              <Botao
                key={id}
                tamanho="pequeno"
                variante={(id === 'calendario') === calendario ? 'primario' : 'secundario'}
                aria-pressed={(id === 'calendario') === calendario}
                onClick={() => trocar('modo', id === 'lista' ? null : id)}
              >
                {rotulo}
              </Botao>
            ))}
          </div>
          <div role="group" aria-label="De quem" className="flex gap-1">
            {(
              [
                ['eu', 'Minhas'],
                ['todos', 'Todas'],
              ] as const
            ).map(([id, rotulo]) => (
              <Botao
                key={id}
                tamanho="pequeno"
                variante={responsavel === id ? 'primario' : 'secundario'}
                aria-pressed={responsavel === id}
                onClick={() => trocar('de', id === 'eu' ? null : id)}
              >
                {rotulo}
              </Botao>
            ))}
          </div>
        </div>
      }
    >
      {!calendario && (
        <div role="group" aria-label="Quais atividades" className="flex flex-wrap gap-2">
          {abas.map(([id, rotulo]) => (
            <Botao
              key={id}
              tamanho="pequeno"
              variante={visao === id ? 'primario' : 'secundario'}
              aria-pressed={visao === id}
              onClick={() => trocar('visao', id === 'hoje' ? null : id)}
            >
              {rotulo}
            </Botao>
          ))}
        </div>
      )}

      {calendario ? (
        doPeriodo.isError ? (
          <EstadoErro
            titulo="Não foi possível carregar as atividades"
            mensagem={doPeriodo.error.message}
            aoTentarNovamente={() => void doPeriodo.refetch()}
          />
        ) : (
          <CalendarioDeAtividades
            mes={mes}
            dias={dias}
            atividades={doPeriodo.data?.itens ?? []}
            carregando={doPeriodo.isPending || doPeriodo.isPlaceholderData}
            aoMudarMes={mudar}
            aoIrParaHoje={irParaHoje}
            aoAbrir={setEditando}
          />
        )
      ) : consulta.isError ? (
        <EstadoErro
          titulo="Não foi possível carregar as atividades"
          mensagem={consulta.error.message}
          aoTentarNovamente={() => void consulta.refetch()}
        />
      ) : dados === undefined ? (
        <EstadoCarregando contexto="as atividades" />
      ) : dados.itens.length === 0 ? (
        <EstadoVazio
          titulo={
            visao === 'hoje'
              ? 'Nada para hoje'
              : visao === 'proximas'
                ? 'Nada agendado'
                : 'Nada concluído ainda'
          }
          descricao={
            visao === 'hoje'
              ? 'Nenhuma atividade vence hoje e nenhuma está atrasada.'
              : 'As atividades nascem dentro dos cards do pipeline.'
          }
        />
      ) : visao === 'hoje' ? (
        <div className="flex flex-col gap-6">
          {atrasadas.length > 0 && (
            <section className="flex flex-col gap-3">
              <h2 className="text-sm font-semibold text-erro">Atrasadas ({atrasadas.length})</h2>
              {lista(atrasadas)}
            </section>
          )}
          <section className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-conteudo-primario">Hoje ({deHoje.length})</h2>
            {deHoje.length === 0 ? (
              <p className="text-sm text-conteudo-secundario">Nada vence hoje.</p>
            ) : (
              lista(deHoje)
            )}
          </section>
        </div>
      ) : (
        lista(dados.itens)
      )}

      {editando !== null && (
        <FormularioDeAtividade atividade={editando} aoFechar={() => setEditando(null)} />
      )}
    </Pagina>
  );
}
